import * as React from 'react';
import * as H from 'history';
import { SetFilterValuesFunction } from '../../../FilterManager';
import { COMMON_DATA_FILTERS } from '../data_constants';
import ClearFiltersButton from '../../../../widgets/ClearFiltersButton';
import DataFilterDropdown from './DataFilterDropdown';
import LocationFilterDropdown from './LocationFilterDropdown';
import SensorFilterDropdown from './SensorFilterDropdown';
import DirectionDropdown from './DirectionDropdown';
import { SeenDict } from '../BaseDataView';

const FILTERS_TO_PRESERVE = Object.values(COMMON_DATA_FILTERS);

export enum FilterDropdownTypes {
  DATA = 'data',
  LOCATION = 'location',
  SENSOR = 'sensor',
  DIRECTION = 'direction',
}

type DataFilter = {
  type: 'data';
  label: string;
  filterKey: string;
  minWidth: number;
  placeholder: string;
  seenKey: string;
  tooltip?: any;
};

type LocationFilter = {
  type: 'location';
  filterKey: string;
};

type SensorFilter = {
  type: 'sensor';
  filterKey: string;
};

type DirectionFilter = {
  type: 'direction';
  filterKey: string;
};

export type FilterDropdown =
  | DataFilter
  | LocationFilter
  | SensorFilter
  | DirectionFilter;

export type FilterDropdownProps = {
  filterKey: string;
  setFilter: (key: string, value: string | null) => void;
  value: string | null;
};

export type Props = {
  filterKeys: Array<string>;
  filters: URLSearchParams;
  history: H.History;
  setFilterValues: SetFilterValuesFunction;
  filterDropdowns: Array<FilterDropdown>;
  seen: SeenDict;
};

export default class AdditionalFilterBar extends React.Component<Props> {
  setFilter = (key: string, value: string | null) => {
    this.props.setFilterValues([{ key, value }]);
  };

  /**
   * Clears the 'Additional Filters' in the filter bar, without impacting
   * the date range and sort order
   */
  clearFilters = () => {
    const clearedFilters = this.props.filterKeys
      .filter((key) => !FILTERS_TO_PRESERVE.includes(key))
      .map((key) => ({
        key: key,
        value: null,
      }));
    this.props.setFilterValues(clearedFilters);
  };

  /**
   * Returns whether or not any 'additional' filters are set (ignoring time range and sorting)
   */
  areFiltersSet = () =>
    !!this.props.filterKeys
      .filter((k) => !FILTERS_TO_PRESERVE.includes(k))
      .find((key) => this.props.filters.get(key));

  render() {
    const { filters, filterDropdowns, seen } = this.props;
    return (
      <React.Fragment>
        <div className="d-flex flex-row flex-wrap">
          {filterDropdowns.map((f, id) => {
            switch (f.type) {
              case 'data':
                return (
                  <DataFilterDropdown
                    key={id}
                    setFilter={this.setFilter}
                    value={filters.get(f.filterKey)}
                    options={seen[f.seenKey]}
                    {...f}
                  />
                );
              case 'location':
                return (
                  <LocationFilterDropdown
                    key={id}
                    setFilter={this.setFilter}
                    value={filters.get('location')}
                    {...f}
                  />
                );
              case 'sensor':
                return (
                  <SensorFilterDropdown
                    key={id}
                    setFilter={this.setFilter}
                    value={filters.get('sensor')}
                    {...f}
                  />
                );
              case 'direction':
                return (
                  <DirectionDropdown
                    key={id}
                    setFilter={this.setFilter}
                    value={filters.get('direction')}
                    {...f}
                  />
                );
              default:
                return null;
            }
          })}
        </div>
        {this.areFiltersSet() ? (
          <div className="d-flex flex-row-reverse pt-3">
            <ClearFiltersButton onClick={this.clearFilters} />
          </div>
        ) : null}
      </React.Fragment>
    );
  }
}
